// lib/leasing/campaignCard.ts
import type { CampaignDTO, CampaignTargetKind } from '#types/types';

/**
 * What a unified campaign row shows, as pure folds over the DTO.
 *
 * Kept out of the renderer so they can be checked without React or a database. Each of the three
 * legacy renderers grew its own version of these, and they disagreed: the asset one preferred the
 * stored name, the portfolio one used the portfolio name. That divergence is the reason the same
 * campaign read differently depending on which list you found it in, so the unified row folds
 * once, here.
 */

/** The granularity a campaign targets, in the vocabulary the legacy tables use. */
export const CAMPAIGN_LEGACY_ENTITY_TYPE: Record<CampaignTargetKind, string> = {
  PORTFOLIO: 'PortfolioFitCampaign',
  ASSET: 'SourcingCampaign',
};

/**
 * The name a human gave the run, else what it targets, else its id.
 *
 * `targetName` already carries the multi-asset "+N" suffix, resolved server-side against the run's
 * real coverage, so the client never re-derives coverage from `propertyIds` and cannot disagree
 * with the card it was rendered from.
 */
export function campaignLabel(campaign: CampaignDTO, fallback: string): string {
  const given = typeof campaign.config?.name === 'string' ? campaign.config.name.trim() : '';
  if (given) return given;
  const target = typeof campaign.targetName === 'string' ? campaign.targetName.trim() : '';
  if (target) return target;
  return `${fallback} ${campaign.id.slice(0, 8)}`;
}

/**
 * Results, as "visible" plus the hidden remainder.
 *
 * `resultCount` excludes rows a human took off the list and `totalResultCount` does not, so the
 * difference is exactly what dismissal hid. Returning both lets a card say "12 of 200" without a
 * second query, and say plainly "-" when the route did not resolve counts at all, which is not
 * the same thing as a run that found nothing, and must not render as zero.
 */
export function campaignResultCounts(campaign: CampaignDTO): {
  visible: number | null;
  total: number | null;
  hidden: number;
} {
  const visible = typeof campaign.resultCount === 'number' ? campaign.resultCount : null;
  const total = typeof campaign.totalResultCount === 'number' ? campaign.totalResultCount : null;
  const hidden = visible !== null && total !== null && total > visible ? total - visible : 0;
  return { visible, total, hidden };
}

/** A run the user can still stop. Mirrors the three legacy renderers, which all agree on this pair. */
export function isCampaignActive(campaign: CampaignDTO): boolean {
  return campaign.status === 'RUNNING' || campaign.status === 'WAITING';
}

/** A run that has stopped moving, so its result counts are worth refetching once. */
export function isCampaignTerminal(status: unknown): boolean {
  return status === 'COMPLETE' || status === 'FAILED' || status === 'ABORTED';
}

/**
 * The status pill palette, for every campaign surface at once.
 *
 * The unified row and the two legacy renderers each carried a byte-identical copy of this switch,
 * so a colour correction had to land three times or the same status read differently depending on
 * which list you found the run in. Typed as a plain string because the three status unions differ
 * on paper and agree on these five values; anything else falls to the neutral branch.
 */
export function campaignStatusPillClass(status: string): string {
  switch (status) {
    case 'WAITING':
      return 'border-amber-400/30 bg-amber-500/15 text-amber-200';
    case 'RUNNING':
      return 'border-blue-400/25 bg-blue-500/15 text-blue-200';
    case 'COMPLETE':
      return 'border-emerald-400/25 bg-emerald-500/15 text-emerald-200';
    case 'FAILED':
      return 'border-rose-400/30 bg-rose-500/15 text-rose-200';
    case 'ABORTED':
      return 'border-orange-400/35 bg-orange-500/15 text-orange-200';
    default:
      return 'border-white/15 bg-white/8 text-white/70';
  }
}

/**
 * When a run last moved, in the reader's locale. Same three copies, same reason.
 *
 * An unparseable value is echoed back rather than rendered as "Invalid Date": the column is
 * informational and the raw string is more useful than a lie.
 */
export function formatCampaignDateTime(value: string | undefined, lang: 'en' | 'fr'): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(lang === 'fr' ? 'fr-FR' : 'en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

/**
 * What a LEGACY campaign socket payload may safely contribute to the unified `Campaign` row.
 *
 * The unified table is a database projection with no socket leg of its own: every worker and route
 * emits under `SourcingCampaign` / `PortfolioFitCampaign`. The dashboard reads
 * the unified registry, so without a fan-out a running card never leaves `RUNNING`.
 *
 * Only the three fields the two shapes genuinely share are copied. The rest of a legacy DTO
 * (`propertyId`, `leadsCount`, …) has no counterpart here, and `targetKind` and
 * `propertyIds` cannot be derived from it at all, which is why this returns a PATCH for a row that
 * already exists and never enough to mint one. Fields absent from the payload stay absent, per the
 * undefined contract in AGENTS.md 3.1.2: sending `null` would erase what the list route resolved.
 */
export function campaignMirrorPatch(payload: unknown): Partial<CampaignDTO> {
  if (!payload || typeof payload !== 'object') return {};
  const source = payload as Record<string, unknown>;
  const patch: Partial<CampaignDTO> = {};
  if (typeof source.status === 'string') patch.status = source.status as CampaignDTO['status'];
  if (typeof source.updatedAt === 'string') patch.updatedAt = source.updatedAt;
  if (source.metrics !== undefined) patch.metrics = source.metrics;
  return patch;
}
